// This file is automatically generated. Do not edit it directly.
import { createClient, type Session } from '@supabase/supabase-js';
import type { Database } from './types';
import { syncSupabaseSessionCookies } from './sessionCookies';

type StorageAdapter = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type StoredSessionPayload = {
  currentSession?: Session | null;
};

const getBrowserStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const browserStorage = getBrowserStorage();

const createStorageAdapter = (): StorageAdapter => {
  if (browserStorage) {
    const storage = browserStorage;
    return {
      getItem: (key: string) => storage.getItem(key),
      setItem: (key: string, value: string) => {
        storage.setItem(key, value);
        if (key.includes('-auth-token')) {
          try {
            const payload = JSON.parse(value) as StoredSessionPayload | null;
            const session = payload?.currentSession ?? null;
            syncSupabaseSessionCookies(session ?? null);
          } catch (error) {
            if (import.meta.env?.DEV) {
              console.debug('[supabase] failed to mirror auth token cookie', error);
            }
          }
        }
      },
      removeItem: (key: string) => {
        storage.removeItem(key);
        if (key.includes('-auth-token')) {
          syncSupabaseSessionCookies(null);
        }
      },
    };
  }

  const memoryStore: Record<string, string> = {};
  return {
    getItem: (key: string) => (key in memoryStore ? memoryStore[key] : null),
    setItem: (key: string, value: string) => {
      memoryStore[key] = value;
      if (key.includes('-auth-token')) {
        try {
          const payload = JSON.parse(value) as StoredSessionPayload | null;
          const session = payload?.currentSession ?? null;
          syncSupabaseSessionCookies(session ?? null);
        } catch {
          // ignore
        }
      }
    },
    removeItem: (key: string) => {
      delete memoryStore[key];
      if (key.includes('-auth-token')) {
        syncSupabaseSessionCookies(null);
      }
    },
  };
};

const storageAdapter = createStorageAdapter();

const envRecord =
  typeof import.meta !== 'undefined' && typeof import.meta.env === 'object'
    ? (import.meta.env as Record<string, string | boolean | undefined>)
    : {};

const readEnvValue = (key: string): string | undefined => {
  const raw = envRecord[key];
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const supabaseUrl =
  readEnvValue('VITE_SUPABASE_URL') ??
  readEnvValue('PUBLIC_SUPABASE_URL') ??
  readEnvValue('SUPABASE_URL');
const supabaseAnonKey =
  readEnvValue('VITE_SUPABASE_PUBLISHABLE_KEY') ??
  readEnvValue('VITE_SUPABASE_ANON_KEY') ??
  readEnvValue('PUBLIC_SUPABASE_PUBLISHABLE_KEY') ??
  readEnvValue('PUBLIC_SUPABASE_ANON_KEY') ??
  readEnvValue('SUPABASE_PUBLISHABLE_KEY') ??
  readEnvValue('SUPABASE_ANON_KEY');

if (!supabaseUrl) {
  throw new Error('VITE_SUPABASE_URL is required to initialize the Supabase client.');
}

if (!supabaseAnonKey) {
  throw new Error('VITE_SUPABASE_PUBLISHABLE_KEY (or VITE_SUPABASE_ANON_KEY) is required to initialize the Supabase client.');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: storageAdapter,
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    headers: {},
  },
});

// Cache for demo-aware clients to prevent multiple instances
const demoClientCache = new Map<string, typeof supabase>();

// Helper to create client with demo token
export const createDemoAwareClient = (demoToken?: string) => {
  if (!demoToken) {
    return supabase;
  }

  if (demoClientCache.has(demoToken)) {
    return demoClientCache.get(demoToken)!;
  }

  const client = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      storage: storageAdapter,
      persistSession: true,
      autoRefreshToken: true,
    },
    global: {
      headers: {
        'x-demo-token': demoToken,
      },
    },
  });

  demoClientCache.set(demoToken, client);
  return client;
};
